import React, { useState } from 'react';
import '../utils/image-compare-home.css';
import GlyphSvg from './Glyphs';
import { Link } from 'react-router-dom';
import { glyphPaths } from '../icons/icon-paths';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SimpleResponsiveImage } from './SimpleResponsiveImage';

gsap.registerPlugin(ScrollTrigger);

function ImageCompare(props) {
    const [currentGlyph, setCurrentGlyph] = useState('g08');
    const [topImageSrc, setTopImageSrc] = useState('img/compare/b1.png');
    const [bottomImageSrc, setBottomImageSrc] = useState('img/compare/b0.png');
    const [isTransitioning, setIsTransitioning] = useState(false);
    const containerRef = React.useRef();
    const topImageRef = React.useRef();
    const dividerRef = React.useRef();
    const bottomImageRef = React.useRef();
    const buttonContainerRef = React.useRef();
    const textRef = React.useRef();
    const spacerRef = React.useRef();
    const scrollTriggerRef = React.useRef();
    const [userHasDragged, setUserHasDragged] = useState(false);

    const baseCycle = ['g11', 'g10', 'g09', 'g08', 'g05', 'g06'];
    const cycleRate = 2;
    const glyphCycle = Array(cycleRate).fill(baseCycle).flat();

    const setGlyphFromPercent = React.useCallback((percent) => {
        const index = Math.floor((percent / 100) * glyphCycle.length * cycleRate) % glyphCycle.length;
        setCurrentGlyph(glyphCycle[index]);
    }, [glyphCycle]);

    // Setup ScrollTrigger with proper cleanup
    const setupScrollTrigger = React.useCallback(() => {
        const wrapper = document.querySelector('.home-slider-wrapper');
        const container = containerRef.current;
        const topImage = topImageRef.current;
        const divider = dividerRef.current;
        const text = textRef.current;

        if (!wrapper || !container || !topImage || !divider) return;

        // Kill existing ScrollTrigger
        if (scrollTriggerRef.current) {
            scrollTriggerRef.current.kill();
        }

        // Disable scroll animation and center slider on mobile
        const isMobile = window.innerWidth <= 991;
        // Only center the slider if the user has NOT dragged yet
        if (isMobile) {
            if (!userHasDragged) {
                gsap.set(topImage, { clipPath: 'inset(0 50% 0 0)' });
                gsap.set(divider, { left: '50%' });
                setGlyphFromPercent(50);
            }
            return;
        }

        // Create new ScrollTrigger for desktop only
        scrollTriggerRef.current = ScrollTrigger.create({
            trigger: wrapper,
            start: "top bottom",
            end: "bottom top",
            scrub: 1,
            onUpdate: (self) => {
                const progress = self.progress;
                const REVEAL_START = 0.05;
                const REVEAL_END = 0.7;
                const REVEAL_RANGE = REVEAL_END - REVEAL_START;
                
                // Add after-anim to #slider-container at 75% scroll
                const sliderContainer = container;
                const buttonContainer = buttonContainerRef.current;
                if (sliderContainer) {
                    if (progress >= 0.4) {
                        sliderContainer.classList.add('after-anim');
                        if (buttonContainer) buttonContainer.classList.add('after-anim');
                    } else {
                        sliderContainer.classList.remove('after-anim');
                        if (buttonContainer) buttonContainer.classList.remove('after-anim');
                    }
                }
                
                if (progress < REVEAL_START) {
                    // Initial state
                    gsap.set(topImage, { clipPath: `inset(0 100% 0 0)` });
                    gsap.set(divider, { left: `0%` });
                    setGlyphFromPercent(0);
                    if (container && text) {
                        container.classList.remove('after-anim');
                        container.classList.add('before-anim');
                        text.classList.remove('after-anim');
                        text.classList.add('before-anim');
                    }
                } else if (progress >= REVEAL_END) {
                    // Final state: lock slider at fully revealed
                    gsap.set(topImage, { clipPath: `inset(0 0% 0 0)` });
                    gsap.set(divider, { left: `100%` });
                    setGlyphFromPercent(100);
                    if (container && text) {
                        container.classList.remove('before-anim');
                        container.classList.add('after-anim');
                        text.classList.remove('before-anim');
                        text.classList.add('after-anim');
                    }
                    // Do NOT update slider position after REVEAL_END
                } else {
                    // Animated state (only up to REVEAL_END)
                    const normalized = (progress - REVEAL_START) / REVEAL_RANGE;
                    const percent = normalized * 100;
                    gsap.set(topImage, { clipPath: `inset(0 ${100 - percent}% 0 0)` });
                    gsap.set(divider, { left: `${percent}%` });
                    setGlyphFromPercent(percent);
                    if (container && text) {
                        container.classList.remove('before-anim');
                        text.classList.remove('before-anim');
                    }
                }
            }
        });
    }, [setGlyphFromPercent, userHasDragged]);

    // Setup ScrollTrigger
    React.useEffect(() => {
        setupScrollTrigger();

        return () => {
            if (scrollTriggerRef.current) {
                scrollTriggerRef.current.kill();
            }
        };
    }, [setupScrollTrigger]);

    // --- INTERACTIVE SLIDER: Enable drag on both desktop and mobile, but only enable ScrollTrigger on desktop ---
    React.useEffect(() => {
        const container = containerRef.current;
        const topImage = topImageRef.current;
        const divider = dividerRef.current;
        const bottomImage = bottomImageRef.current;
        const buttons = buttonContainerRef.current?.querySelectorAll('button');
        if (!container || !topImage || !divider || !bottomImage) return;
        const isMobile = window.innerWidth <= 991;

        let isDragging = false;
        let scrollTrigger = null;
        // Track if manual drag is active
        let manualDragActive = false;
        // Block ScrollTrigger onUpdate during drag
        let blockScrollTriggerUpdate = false;
        // Store ScrollTrigger instance for recreation
        let scrollTriggerInstance = null;

        const revealAtX = (x) => {
            const rect = container.getBoundingClientRect();
            const percent = Math.max(0, Math.min(100, ((x - rect.left) / rect.width) * 100));
            // Update clip-path for top image
            topImage.style.clipPath = `inset(0 ${100 - percent}% 0 0)`;
            // Update divider position
            divider.style.left = `${percent}%`;
            // Update glyph based on position
            const glyphIndex = Math.floor((percent / 100) * (glyphCycle.length - 1));
            setCurrentGlyph(glyphCycle[glyphIndex]);
        };

        // Only attach ScrollTrigger and scroll-based logic on desktop
        if (!isMobile) {
            const wrapper = document.querySelector('.home-slider-wrapper');
            if (wrapper) {
                scrollTriggerInstance = ScrollTrigger.create({
                    trigger: wrapper,
                    start: "top bottom",
                    end: "bottom top",
                    scrub: 1,
                    onUpdate: (self) => {
                        if (isDragging || manualDragActive || blockScrollTriggerUpdate) return;
                        const progress = self.progress;
                        const REVEAL_START = 0.05;
                        const REVEAL_END = 0.7;
                        const REVEAL_RANGE = REVEAL_END - REVEAL_START;
                        // Add after-anim to #slider-container at 75% scroll
                        const sliderContainer = container;
                        const buttonContainer = buttonContainerRef.current;
                        if (sliderContainer) {
                            if (progress >= 0.4) {
                                sliderContainer.classList.add('after-anim');
                                if (buttonContainer) buttonContainer.classList.add('after-anim');
                            } else {
                                sliderContainer.classList.remove('after-anim');
                                if (buttonContainer) buttonContainer.classList.remove('after-anim');
                            }
                        }
                        if (progress < REVEAL_START) {
                            // Initial state
                            gsap.set(topImage, { clipPath: `inset(0 100% 0 0)` });
                            gsap.set(divider, { left: `0%` });
                            setGlyphFromPercent(0);
                            if (container && textRef.current) {
                                container.classList.remove('after-anim');
                                container.classList.add('before-anim');
                                textRef.current.classList.remove('after-anim');
                                textRef.current.classList.add('before-anim');
                            }
                        } else if (progress >= REVEAL_END) {
                            // Final state
                            gsap.set(topImage, { clipPath: `inset(0 0% 0 0)` });
                            gsap.set(divider, { left: `100%` });
                            setGlyphFromPercent(100);
                            if (container && textRef.current) {
                                container.classList.remove('before-anim');
                                container.classList.add('after-anim');
                                textRef.current.classList.remove('before-anim');
                                textRef.current.classList.add('after-anim');
                            }
                        } else {
                            // Animated state
                            const normalized = (progress - REVEAL_START) / REVEAL_RANGE;
                            const percent = normalized * 100;
                            gsap.set(topImage, { clipPath: `inset(0 ${100 - percent}% 0 0)` });
                            gsap.set(divider, { left: `${percent}%` });
                            setGlyphFromPercent(percent);
                            if (container && textRef.current) {
                                container.classList.remove('before-anim');
                                textRef.current.classList.remove('before-anim');
                            }
                        }
                    }
                });
                scrollTrigger = scrollTriggerInstance;
            }
        }

        // --- Manual drag logic (works on both desktop and mobile) ---
        const onMouseDown = (e) => {
            e.preventDefault();
            e.stopPropagation();
            isDragging = true;
            manualDragActive = true;
            blockScrollTriggerUpdate = true;
            setUserHasDragged(true);
            // Pause and kill ScrollTrigger when dragging starts (desktop only)
            if (!isMobile && scrollTrigger) {
                scrollTrigger.disable();
                scrollTrigger.kill();
                scrollTrigger = null;
                scrollTriggerInstance = null;
            }
            // Force overflow-x: hidden during drag
            container.style.overflowX = 'hidden';
            revealAtX(e.clientX);
        };

        const onMouseMove = (e) => {
            if (!isDragging) return;
            e.preventDefault();
            e.stopPropagation();
            // Keep overflow-x: hidden during drag
            container.style.overflowX = 'hidden';
            revealAtX(e.clientX);
        };

        const onMouseUp = (e) => {
            if (!isDragging) return;
            e.preventDefault();
            e.stopPropagation();
            isDragging = false;
            blockScrollTriggerUpdate = false;
            // Do NOT re-enable or recreate ScrollTrigger here; keep manual position
            // Ensure overflow-x: hidden after drag
            container.style.overflowX = 'hidden';
        };

        // Touch support - VERSION 8 FIX: Direct touch handling without gesture detection
        const onTouchStart = (e) => {
            e.preventDefault();
            e.stopPropagation();
            isDragging = true;
            manualDragActive = true;
            blockScrollTriggerUpdate = true;
            setUserHasDragged(true); // Always set to true on any drag/touch
            // Pause and kill ScrollTrigger when dragging starts (desktop only)
            if (!isMobile && scrollTrigger) {
                scrollTrigger.disable();
                scrollTrigger.kill();
                scrollTrigger = null;
                scrollTriggerInstance = null;
            }
            // Force overflow-x: hidden during drag
            container.style.overflowX = 'hidden';
            // VERSION 8 FIX: Start from actual touch position immediately
            if (e.touches.length > 0) {
                revealAtX(e.touches[0].clientX);
            }
        };

        const onTouchMove = (e) => {
            if (!isDragging || e.touches.length === 0) return;
            e.preventDefault();
            e.stopPropagation();
            // Keep overflow-x: hidden during drag
            container.style.overflowX = 'hidden';
            // VERSION 8 FIX: Direct touch position update
            revealAtX(e.touches[0].clientX);
        };

        const onTouchEnd = (e) => {
            if (!isDragging) return;
            e.preventDefault();
            e.stopPropagation();
            isDragging = false;
            blockScrollTriggerUpdate = false;
            // Do NOT re-enable or recreate ScrollTrigger here; keep manual position
            // Ensure overflow-x: hidden after drag
            container.style.overflowX = 'hidden';
        };

        // Only attach scroll event handler for desktop
        let onScroll;
        if (!isMobile) {
            // Re-enable ScrollTrigger only when user scrolls the page
            onScroll = () => {
                if (manualDragActive) {
                    // Recreate ScrollTrigger on scroll
                    const wrapper = document.querySelector('.home-slider-wrapper');
                    if (!scrollTrigger && wrapper) {
                        scrollTriggerInstance = ScrollTrigger.create({
                            trigger: wrapper,
                            start: "top bottom",
                            end: "bottom top",
                            scrub: 1,
                            onUpdate: (self) => {
                                if (isDragging || manualDragActive || blockScrollTriggerUpdate) return;
                                const progress = self.progress;
                                const REVEAL_START = 0.05;
                                const REVEAL_END = 0.7;
                                const REVEAL_RANGE = REVEAL_END - REVEAL_START;
                                // Add after-anim to #slider-container at 75% scroll
                                const sliderContainer = container;
                                const buttonContainer = buttonContainerRef.current;
                                if (sliderContainer) {
                                    if (progress >= 0.4) {
                                        sliderContainer.classList.add('after-anim');
                                        if (buttonContainer) buttonContainer.classList.add('after-anim');
                                    } else {
                                        sliderContainer.classList.remove('after-anim');
                                        if (buttonContainer) buttonContainer.classList.remove('after-anim');
                                    }
                                }
                                if (progress < REVEAL_START) {
                                    // Initial state
                                    gsap.set(topImage, { clipPath: `inset(0 100% 0 0)` });
                                    gsap.set(divider, { left: `0%` });
                                    setGlyphFromPercent(0);
                                    if (container && textRef.current) {
                                        container.classList.remove('after-anim');
                                        container.classList.add('before-anim');
                                        textRef.current.classList.remove('after-anim');
                                        textRef.current.classList.add('before-anim');
                                    }
                                } else if (progress >= REVEAL_END) {
                                    // Final state
                                    gsap.set(topImage, { clipPath: `inset(0 0% 0 0)` });
                                    gsap.set(divider, { left: `100%` });
                                    setGlyphFromPercent(100);
                                    if (container && textRef.current) {
                                        container.classList.remove('before-anim');
                                        container.classList.add('after-anim');
                                        textRef.current.classList.remove('before-anim');
                                        textRef.current.classList.add('after-anim');
                                    }
                                } else {
                                    // Animated state
                                    const normalized = (progress - REVEAL_START) / REVEAL_RANGE;
                                    const percent = normalized * 100;
                                    gsap.set(topImage, { clipPath: `inset(0 ${100 - percent}% 0 0)` });
                                    gsap.set(divider, { left: `${percent}%` });
                                    setGlyphFromPercent(percent);
                                    if (container && textRef.current) {
                                        container.classList.remove('before-anim');
                                        textRef.current.classList.remove('before-anim');
                                    }
                                }
                            }
                        });
                        scrollTrigger = scrollTriggerInstance;
                    }
                    manualDragActive = false;
                    blockScrollTriggerUpdate = false;
                }
            };
            window.addEventListener('scroll', onScroll, { passive: true });
        }

        // Add event listeners with capture phase (works everywhere)
        container.addEventListener('mousedown', onMouseDown, { capture: true, passive: false });
        container.addEventListener('mousemove', onMouseMove, { capture: true, passive: false });
        container.addEventListener('mouseup', onMouseUp, { capture: true, passive: false });
        container.addEventListener('touchstart', onTouchStart, { capture: true, passive: false });
        container.addEventListener('touchmove', onTouchMove, { capture: true, passive: false });
        container.addEventListener('touchend', onTouchEnd, { capture: true, passive: false });

        // Button click logic with smooth crossfade
        buttons?.forEach(button => {
            button.addEventListener('click', () => {
                const newTop = button.getAttribute('data-top');
                const newBottom = button.getAttribute('data-bottom');
                if (!newTop || !newBottom || isTransitioning) return;

                if (
                    topImageSrc === newTop &&
                    bottomImageSrc === newBottom
                ) return;

                // Disable ScrollTrigger when button is clicked (desktop only)
                if (!isMobile && scrollTrigger) {
                    scrollTrigger.disable();
                }

                // Capture userHasDragged at the moment of click
                const shouldCenter = !userHasDragged;
                setIsTransitioning(true);
                buttons.forEach(btn => btn.classList.remove('selected'));
                button.classList.add('selected');

                // Smooth crossfade transition
                const tl = gsap.timeline({
                    onComplete: () => {
                        setIsTransitioning(false);
                        // Always center the slider after image change
                        requestAnimationFrame(() => {
                            gsap.to(topImage, { 
                                clipPath: `inset(0 50% 0 0)`,
                                duration: 0.5,
                                ease: "power2.out"
                            });
                            gsap.to(divider, { 
                                left: `50%`,
                                duration: 0.5,
                                ease: "power2.out"
                            });
                            setGlyphFromPercent(50);
                        });
                        setUserHasDragged(false); // Reset after button change
                        // Re-enable ScrollTrigger after transition completes (desktop only)
                        if (!isMobile && scrollTrigger) {
                            scrollTrigger.enable();
                        }
                    }
                });

                // Fade out current images
                tl.to([topImage, bottomImage], {
                    opacity: 0,
                    duration: 0.3,
                    ease: "power2.out"
                });

                // Update image sources
                tl.call(() => {
                    setBottomImageSrc(newBottom);
                    setTopImageSrc(newTop);
                });

                // Fade in new images
                tl.to([topImage, bottomImage], {
                    opacity: 1,
                    duration: 0.4,
                    ease: "power2.in"
                });
            });
        });

        return () => {
            container.removeEventListener('mousedown', onMouseDown);
            container.removeEventListener('mousemove', onMouseMove);
            container.removeEventListener('mouseup', onMouseUp);
            container.removeEventListener('touchstart', onTouchStart);
            container.removeEventListener('touchmove', onTouchMove);
            container.removeEventListener('touchend', onTouchEnd);
            if (!isMobile && onScroll) {
                window.removeEventListener('scroll', onScroll);
            }
            // Clean up ScrollTrigger
            if (scrollTrigger) {
                scrollTrigger.kill();
            }
        };
    }, [topImageSrc, bottomImageSrc, isTransitioning, glyphCycle, userHasDragged]);

    return (
        <div className="home-slider-wrapper" ref={spacerRef} style={{ position: 'relative' }}>
            {/* Sticky full-screen container during scroll */}
            <div
                className="sticky-compare-container"
                style={{
                    position: 'sticky',
                    top: 0,
                    height: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10
                }}
            >
                <div id="home-image-compare" className="w-100">
                    <div id="home-slider-container"
                        ref={containerRef}
                        className=""
                    >
                        <div id="home-slider-divider" ref={dividerRef}>
                            <svg
                                className="slider-icon"
                                width="24"
                                height="24"
                                fill="var(--c4)"
                                viewBox="0 0 24 24"
                                dangerouslySetInnerHTML={{ __html: glyphPaths[currentGlyph] }}
                            />
                        </div>
                        <SimpleResponsiveImage
                          id="home-image-bottom"
                          ref={bottomImageRef}
                          src={bottomImageSrc}
                          alt="Bottom"
                          widths={[800, 1200, 1600]}
                          quality={85}
                          sizes="100vw"
                        />
                        <SimpleResponsiveImage
                          id="home-image-top"
                          ref={topImageRef}
                          src={topImageSrc}
                          alt="Top"
                          widths={[800, 1200, 1600]}
                          quality={85}
                          sizes="100vw"
                        />
                        <div id="home-slider-mask" />
                    </div>
                    
                    {/* Compare Buttons */}
                    <div id="home-image-buttons" ref={buttonContainerRef}>
                        <button className="selected" data-top="img/compare/b1.png" data-bottom="img/compare/b0.png">
                            <GlyphSvg size="12" glyph="g09" color="black" /> &nbsp;
                            Body
                        </button>
                        <button data-top="img/compare/face1.png" data-bottom="img/compare/face0.png">
                            <GlyphSvg size="12" glyph="g10" color="black" /> &nbsp;
                            Face
                        </button>
                        {/* <button data-top="img/compare/hand1.png" data-bottom="img/compare/hand0.png">
                            <GlyphSvg size="12" glyph="g11" color="black" /> &nbsp;
                            Hands
                        </button> */}
                    </div>
                    
                    {/* Compare Text - Mobile First */}
                    <div className="compare-text d-md-none" ref={textRef}>
                        <h4 className='c-c00 f-uc'>Hyperrealism</h4>
                        <p className='c-c00'>Our avatars feature lifelike, intricate details, blurring the borders of reality and AI.</p>
                        <Link
                            to="/avatars"
                            className="btn btn-link py-2 px-3 mb-3 b-c0 o-c00 c-c00"
                        >
                            View Avatars
                        </Link>
                    </div>
                    
                    {/* Compare Text - Desktop */}
                    <div className="compare-text d-none d-md-block" ref={textRef}>
                    <h4 className='c-c00 f-uc'>Hyperrealism</h4>
                        <p className='c-c00'>Our avatars feature lifelike, intricate details, blurring the borders of reality and AI.</p>
                        <Link
                            to="/avatars"
                            className="btn btn-link py-2 px-3 mb-3 b-c0 o-c00 c-c00"
                        >
                            View Avatars
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ImageCompare; 